import {
  StackHealth,
  InterfaceHealthColor,
  InterfaceHealth,
  CustomerAgencyInterfaceHealthStatuses,
} from '../api'
import { Customer } from 'contexts/base'
import { CustomerInterface } from 'contexts/CurrentCustomerContext'

export type ConnectorHealth = {
  [key: string]: InterfaceHealthColor
}

export type StackHealthPerConnector = {
  [key: string]: InterfaceHealth
}

export const updateHealthInConnectorList: (
  connectors: CustomerInterface[],
  healthInfo: StackHealthPerConnector,
) => ConnectorHealth = (
  connectors: CustomerInterface[],
  healthInfo: StackHealthPerConnector,
) => {
  return connectors.reduce(
    (accum: ConnectorHealth, current: CustomerInterface) => ({
      ...accum,
      [current.id]: healthInfo[current.id]
        ? healthInfo[current.id].healthCheckStatus.healthColor
        : InterfaceHealthColor.Black,
    }),
    {} as ConnectorHealth,
  )
}

/**
 * Typescript enums create objects with both the numeric keys as well as string keys
 * Ensure that we are always using the numeric version
 * @param health
 */
export const ensureHealthColorIsEnumValue = (health: InterfaceHealthColor) => {
  // health could be a numeric value, or a color value like Green, Black, Red, etc...
  // map either of those to the enum value
  let mappedEnumValue = health
  if (isNaN(parseInt(health.toString(), 10))) {
    // we got a color health value
    mappedEnumValue = (InterfaceHealthColor[
      health as any
    ] as unknown) as InterfaceHealthColor
  }

  return mappedEnumValue
}

const chooseWorseHealth = (
  oldHealth: InterfaceHealthColor,
  newHealth: InterfaceHealthColor,
) => {
  const oldHealthEnumValue = ensureHealthColorIsEnumValue(oldHealth)
  const newHealthEnumValue = ensureHealthColorIsEnumValue(newHealth)
  if (
    oldHealthEnumValue === InterfaceHealthColor.Black ||
    newHealthEnumValue === InterfaceHealthColor.Black
  )
    return InterfaceHealthColor.Black

  if (
    oldHealthEnumValue === InterfaceHealthColor.Red ||
    newHealthEnumValue === InterfaceHealthColor.Red
  )
    return InterfaceHealthColor.Red

  if (
    oldHealthEnumValue === InterfaceHealthColor.Orange ||
    newHealthEnumValue === InterfaceHealthColor.Orange
  )
    return InterfaceHealthColor.Orange

  if (
    oldHealthEnumValue === InterfaceHealthColor.Yellow ||
    newHealthEnumValue === InterfaceHealthColor.Yellow
  )
    return InterfaceHealthColor.Yellow

  return InterfaceHealthColor.Green
}

export const setCustomerHealth = (
  health: CustomerAgencyInterfaceHealthStatuses,
  customers: Customer[],
) => {
  const customersHealth = health

  // if there weren't any health changes then return the exact same reference as an optimization
  // otherwise the customer list will "refresh" every 30 seconds even if nothing has changed
  let hasUpdates = false

  const updatedCustomers = customers.map(customer => {
    if (customersHealth[customer.id]) {
      const customerInterfaces = customersHealth[customer.id]
      const minimumConnectorHealth = customerInterfaces.reduce(
        (accum, current) => {
          return chooseWorseHealth(accum, current.healthColor)
        },
        InterfaceHealthColor.Green,
      )

      if (customer.minimumConnectorHealth === minimumConnectorHealth) {
        return customer
      }

      hasUpdates = true
      return {
        ...customer,
        minimumConnectorHealth,
      }
    }

    if (customer.minimumConnectorHealth === InterfaceHealthColor.Black) {
      return customer
    }

    return {
      ...customer,
      minimumConnectorHealth: InterfaceHealthColor.Black,
    }
  })

  if (hasUpdates) {
    return updatedCustomers
  }

  return customers
}
